if (!customElements.get('craft-story-carousel')) {
  customElements.define(
    'craft-story-carousel',
    class CraftStoryCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.compactQuery = window.matchMedia('(max-width: 749px)');
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplayRequested = this.dataset.autoplay === 'true';
        this.autoplayInterval = Math.max(3000, Number(this.dataset.autoplayInterval) || 6000);
        this.inView = !('IntersectionObserver' in window);
        this.hoverPaused = false;
        this.focusPaused = false;
        this.editorPaused = false;
        this.gesturePaused = false;
        this.gestureAutoplayRemaining = null;
        this.dragState = null;
        this.suppressNextStageClick = false;
        this.preferTouchEvents =
          'ontouchstart' in window || (navigator.maxTouchPoints > 0 && 'TouchEvent' in window);
        this.touchMoveOptions = { passive: false };

        this.onClick = this.handleClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onPointerEnter = () => this.setPauseState('hoverPaused', true);
        this.onPointerLeave = () => this.setPauseState('hoverPaused', false);
        this.onFocusIn = () => this.setPauseState('focusPaused', true);
        this.onFocusOut = this.handleFocusOut.bind(this);
        this.onVisibilityChange = this.updateAutoplay.bind(this);
        this.onViewportChange = this.handleViewportChange.bind(this);
        this.onMotionChange = this.handleMotionChange.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.onBlockDeselect = this.handleBlockDeselect.bind(this);
        this.onSectionSelect = this.handleSectionSelect.bind(this);
        this.onSectionDeselect = this.handleSectionDeselect.bind(this);
        this.onMutations = this.handleMutations.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onPointerCancel = this.handlePointerCancel.bind(this);
        this.onWindowPointerDown = this.handleWindowPointerDown.bind(this);
        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchMove = this.handleTouchMove.bind(this);
        this.onTouchEnd = this.handleTouchEnd.bind(this);
        this.onTouchCancel = this.handleTouchCancel.bind(this);
        this.onWindowTouchStart = this.handleWindowTouchStart.bind(this);
        this.onWindowBlur = () => this.clearDragState(undefined, true);
        this.onLostPointerCapture = this.handleLostPointerCapture.bind(this);
        this.onStageClickCapture = this.handleStageClickCapture.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        this.addMediaListener(this.compactQuery, this.onViewportChange);
        this.addMediaListener(this.desktopQuery, this.onViewportChange);
        this.addMediaListener(this.reducedMotionQuery, this.onMotionChange);

        if ('MutationObserver' in window) {
          this.mutationObserver = new MutationObserver(this.onMutations);
          this.mutationObserver.observe(this, { childList: true, subtree: true });
        }

        this.refresh();
        if (!this.cards.length) return;

        this.initialized = true;
        this.classList.add('is-enhanced');
        this.activeIndex = Math.max(0, this.cards.findIndex((card) => card.dataset.craftActive === 'true'));
        this.activate(this.activeIndex);
        this.observeVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        this.removeMediaListener(this.compactQuery, this.onViewportChange);
        this.removeMediaListener(this.desktopQuery, this.onViewportChange);
        this.removeMediaListener(this.reducedMotionQuery, this.onMotionChange);
        this.visibilityObserver?.disconnect();
        this.mutationObserver?.disconnect();
        window.cancelAnimationFrame(this.mutationFrame);
        window.clearTimeout(this.suppressClickTimer);
        this.suppressNextStageClick = false;
        this.bindGestureStage(null, false);
        this.stopAutoplay();
        this.initialized = false;
      }

      addMediaListener(query, listener) {
        if (query.addEventListener) query.addEventListener('change', listener);
        else query.addListener(listener);
      }

      removeMediaListener(query, listener) {
        if (query?.removeEventListener) query.removeEventListener('change', listener);
        else query?.removeListener(listener);
      }

      refresh() {
        this.cards = [...this.querySelectorAll('[data-craft-card]')];
        this.selectors = [...this.querySelectorAll('[data-craft-selector]')];
        this.bindGestureStage(this.querySelector('[data-craft-stage]'));
      }

      bindGestureStage(nextStage, resumeAutoplay = true) {
        if (this.stage === nextStage) return;
        this.clearDragState(undefined, resumeAutoplay);

        if (this.stage) {
          this.stage.removeEventListener('pointerdown', this.onPointerDown);
          this.stage.removeEventListener('touchstart', this.onTouchStart);
          this.stage.removeEventListener('touchmove', this.onTouchMove, this.touchMoveOptions);
          this.stage.removeEventListener('touchend', this.onTouchEnd);
          this.stage.removeEventListener('touchcancel', this.onTouchCancel);
          this.stage.removeEventListener('lostpointercapture', this.onLostPointerCapture);
          this.stage.removeEventListener('click', this.onStageClickCapture, true);
        }

        this.stage = nextStage;
        if (!this.stage) return;

        this.stage.addEventListener('pointerdown', this.onPointerDown);
        this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.stage.addEventListener('touchmove', this.onTouchMove, this.touchMoveOptions);
        this.stage.addEventListener('touchend', this.onTouchEnd);
        this.stage.addEventListener('touchcancel', this.onTouchCancel);
        this.stage.addEventListener('lostpointercapture', this.onLostPointerCapture);
        this.stage.addEventListener('click', this.onStageClickCapture, true);
      }

      handleClick(event) {
        const selector = event.target.closest('[data-craft-selector]');
        if (selector && this.contains(selector)) {
          this.refresh();
          this.activate(this.selectors.indexOf(selector), { manual: true });
          return;
        }

        const card = event.target.closest('[data-craft-card]');
        if (!card || !this.contains(card)) return;
        this.refresh();
        const index = this.cards.indexOf(card);
        if (index !== this.activeIndex) this.activate(index, { manual: true });
      }

      handlePointerDown(event) {
        if (event.pointerType === 'touch' && this.preferTouchEvents) return;
        if (this.dragState && this.dragState.pointerId !== event.pointerId) {
          this.clearDragState(undefined, true);
          return;
        }
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || this.cards.length < 2) return;

        const interactiveControl = event.target.closest?.(
          'a, button, input, select, textarea, summary, [role="button"], [contenteditable="true"], [data-no-swipe]'
        );
        if (interactiveControl && event.currentTarget.contains(interactiveControl)) return;

        this.startGesture('pointer', event.pointerId, event.currentTarget, event.clientX, event.clientY, event.timeStamp);
      }

      handleTouchStart(event) {
        if (!this.preferTouchEvents || this.cards.length < 2) return;
        if (this.dragState || event.touches.length !== 1) {
          this.clearDragState(undefined, true);
          return;
        }

        const interactiveControl = event.target.closest?.(
          'a, button, input, select, textarea, summary, [role="button"], [contenteditable="true"], [data-no-swipe]'
        );
        if (interactiveControl && event.currentTarget.contains(interactiveControl)) return;

        const touch = event.changedTouches[0] || event.touches[0];
        if (!touch) return;
        this.startGesture('touch', touch.identifier, event.currentTarget, touch.clientX, touch.clientY, event.timeStamp);
      }

      startGesture(inputType, pointerId, stage, clientX, clientY, timeStamp) {
        const stageRect = stage.getBoundingClientRect();
        this.pauseAutoplayForGesture();

        this.dragState = {
          inputType,
          pointerId,
          stage,
          startX: clientX,
          startY: clientY,
          currentX: clientX,
          currentY: clientY,
          startTime: timeStamp,
          stageWidth: stageRect.width,
          horizontalIntent: false,
          captureActive: false,
        };

        if (inputType === 'touch') {
          window.addEventListener('touchstart', this.onWindowTouchStart, { passive: true });
        } else {
          window.addEventListener('pointerdown', this.onWindowPointerDown);
          window.addEventListener('pointermove', this.onPointerMove);
          window.addEventListener('pointerup', this.onPointerUp);
          window.addEventListener('pointercancel', this.onPointerCancel);
        }
        window.addEventListener('blur', this.onWindowBlur);
      }

      handleWindowPointerDown(event) {
        if (
          this.dragState?.inputType === 'pointer' &&
          this.dragState.pointerId !== event.pointerId
        ) {
          this.clearDragState(undefined, true);
        }
      }

      handleWindowTouchStart(event) {
        if (this.dragState?.inputType === 'touch' && event.touches.length !== 1) {
          this.clearDragState(undefined, true);
        }
      }

      handlePointerMove(event) {
        this.updateGesture(event, 'pointer', event.pointerId, event.clientX, event.clientY);
      }

      handleTouchMove(event) {
        const drag = this.dragState;
        if (!drag || drag.inputType !== 'touch') return;
        if (event.touches.length !== 1) {
          this.clearDragState(undefined, true);
          return;
        }

        const touch = this.findTouch(event.touches, drag.pointerId);
        if (!touch) return;
        this.updateGesture(event, 'touch', touch.identifier, touch.clientX, touch.clientY);
      }

      updateGesture(event, inputType, pointerId, clientX, clientY) {
        const drag = this.dragState;
        if (!drag || drag.inputType !== inputType || drag.pointerId !== pointerId) return;

        const deltaX = clientX - drag.startX;
        const deltaY = clientY - drag.startY;
        drag.currentX = clientX;
        drag.currentY = clientY;
        const horizontalTravel = Math.abs(deltaX);
        const verticalTravel = Math.abs(deltaY);
        if (!drag.horizontalIntent) {
          if (horizontalTravel < 8 && verticalTravel < 8) return;
          if (verticalTravel >= 12 && verticalTravel > horizontalTravel * 1.25) {
            this.clearDragState(pointerId, true);
            return;
          }
          if (horizontalTravel < 8 || horizontalTravel <= verticalTravel * 1.2) return;

          drag.horizontalIntent = true;
          drag.stage.classList.add('is-dragging');
          if (inputType === 'pointer') {
            try {
              drag.stage.setPointerCapture?.(pointerId);
            } catch (error) {
              if (error.name !== 'NotFoundError' && error.name !== 'InvalidStateError') throw error;
            }
            drag.captureActive = drag.stage.hasPointerCapture?.(pointerId) === true;
          }
        }

        if (event.cancelable) event.preventDefault();
      }

      handlePointerUp(event) {
        this.finishGesture('pointer', event.pointerId, event.clientX, event.clientY, event.timeStamp);
      }

      handleTouchEnd(event) {
        const drag = this.dragState;
        if (!drag || drag.inputType !== 'touch') return;
        const touch = this.findTouch(event.changedTouches, drag.pointerId);
        if (!touch) return;
        this.finishGesture('touch', touch.identifier, touch.clientX, touch.clientY, event.timeStamp);
      }

      finishGesture(inputType, pointerId, clientX, clientY, timeStamp) {
        const drag = this.dragState;
        if (!drag || drag.inputType !== inputType || drag.pointerId !== pointerId) return;

        const deltaX = clientX - drag.startX;
        const deltaY = clientY - drag.startY;
        const elapsed = Math.max(1, timeStamp - drag.startTime);
        const horizontalTravel = Math.abs(deltaX);
        const threshold = Math.min(52, Math.max(40, drag.stageWidth * 0.12));
        const isFlick = horizontalTravel >= 26 && horizontalTravel / elapsed >= 0.4;
        const shouldNavigate =
          drag.horizontalIntent &&
          horizontalTravel > Math.abs(deltaY) * 1.2 &&
          (horizontalTravel >= threshold || isFlick);
        this.clearDragState(pointerId, !shouldNavigate);
        if (!shouldNavigate) return;

        this.suppressNextStageClick = true;
        window.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = window.setTimeout(() => {
          this.suppressNextStageClick = false;
        }, 400);
        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = (this.activeIndex + direction + this.cards.length) % this.cards.length;
        this.activate(nextIndex, { manual: true });
      }

      handlePointerCancel(event) {
        if (this.dragState?.inputType === 'pointer' && this.dragState.pointerId === event.pointerId) {
          this.clearDragState(event.pointerId, true);
        }
      }

      handleTouchCancel() {
        if (this.dragState?.inputType === 'touch') this.clearDragState(undefined, true);
      }

      handleLostPointerCapture(event) {
        if (this.dragState?.inputType === 'pointer' && this.dragState.pointerId === event.pointerId) {
          this.clearDragState(event.pointerId, true);
        }
      }

      findTouch(touchList, identifier) {
        for (let index = 0; index < touchList.length; index += 1) {
          if (touchList[index].identifier === identifier) return touchList[index];
        }
        return null;
      }

      handleStageClickCapture(event) {
        if (!this.suppressNextStageClick) return;
        this.suppressNextStageClick = false;
        window.clearTimeout(this.suppressClickTimer);
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      clearDragState(pointerId, resumeAutoplay = true) {
        if (!this.dragState) return;
        const { stage, inputType, pointerId: activePointerId } = this.dragState;
        this.dragState = null;
        window.removeEventListener('pointerdown', this.onWindowPointerDown);
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerCancel);
        window.removeEventListener('touchstart', this.onWindowTouchStart);
        window.removeEventListener('blur', this.onWindowBlur);
        stage.classList.remove('is-dragging');
        const pointerToRelease = pointerId ?? activePointerId;
        if (inputType === 'pointer') {
          try {
            if (stage.hasPointerCapture?.(pointerToRelease)) stage.releasePointerCapture(pointerToRelease);
          } catch (error) {
            if (error.name !== 'NotFoundError' && error.name !== 'InvalidStateError') throw error;
          }
        }
        if (resumeAutoplay) this.resumeAutoplayAfterGesture();
        else {
          this.gesturePaused = false;
          this.gestureAutoplayRemaining = null;
        }
      }

      handleKeydown(event) {
        const selector = event.target.closest('[data-craft-selector]');
        if (!selector || !this.contains(selector)) return;

        this.refresh();
        const currentIndex = this.selectors.indexOf(selector);
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.selectors.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.selectors.length) % this.selectors.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.selectors.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        this.activate(nextIndex, { manual: true, focus: true });
      }

      activate(index, options = {}) {
        if (index < 0 || index >= this.cards.length) return;

        this.stopAutoplay();
        this.activeIndex = index;
        const activePanelId = this.cards[index].id;
        this.selectors.forEach((selector) => {
          const active = selector.getAttribute('aria-controls') === activePanelId;
          selector.classList.toggle('is-active', active);
          selector.setAttribute('aria-selected', String(active));
          selector.tabIndex = active ? 0 : -1;
        });

        this.cards.forEach((card, cardIndex) => {
          const active = cardIndex === index;
          card.dataset.craftActive = String(active);
          card.setAttribute('aria-hidden', String(!active));
          card.hidden = false;
        });

        this.positionCards();
        if (options.focus) this.selectors[index]?.focus();
        this.updateAutoplay();
      }

      positionCards() {
        const count = this.cards.length;
        const compact = this.compactQuery.matches;
        const desktop = this.desktopQuery.matches;
        this.cards.forEach((card, cardIndex) => {
          let offset = cardIndex - this.activeIndex;
          if (offset > count / 2) offset -= count;
          if (offset < count / -2) offset += count;

          const depth = Math.abs(offset);
          const direction = Math.sign(offset);
          const xStep = compact ? 20 : desktop ? 30 : 25;
          const xDepthStep = compact ? 4 : desktop ? 7 : 5;
          const zStep = compact ? -70 : desktop ? -110 : -90;
          const rotateBase = compact ? 8 : 10;
          const rotateStep = compact ? 2 : 3;
          const scaleStep = compact ? 0.08 : 0.09;
          const x = offset === 0 ? 0 : direction * (xStep + Math.min(depth - 1, 3) * xDepthStep);
          const y = Math.min(depth * (compact ? 1.5 : 2), compact ? 6 : 8);
          const z = depth * zStep;
          const rotate = offset === 0 ? 0 : direction * -1 * (rotateBase + Math.min(depth, 3) * rotateStep);
          const scale = Math.max(compact ? 0.76 : 0.72, 1 - depth * scaleStep);
          const opacity = Math.max(0.16, 1 - depth * 0.24);

          card.style.setProperty('--craft-x', `${x}%`);
          card.style.setProperty('--craft-y', `${y}%`);
          card.style.setProperty('--craft-z', `${z}px`);
          card.style.setProperty('--craft-rotate', `${rotate}deg`);
          card.style.setProperty('--craft-scale', String(scale));
          card.style.setProperty('--craft-opacity', String(opacity));
          card.style.setProperty('--craft-z-index', String(count - depth));
        });
      }

      handleViewportChange() {
        this.positionCards();
      }

      handleMotionChange() {
        this.positionCards();
        this.updateAutoplay();
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) {
          this.inView = true;
          this.updateAutoplay();
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.3;
            this.updateAutoplay();
          },
          { threshold: [0, 0.3] }
        );
        this.visibilityObserver.observe(this);
      }

      canAutoplay() {
        return (
          this.autoplayRequested &&
          this.cards.length > 1 &&
          !this.reducedMotionQuery.matches &&
          this.inView &&
          !document.hidden &&
          !this.hoverPaused &&
          !this.focusPaused &&
          !this.editorPaused &&
          !this.gesturePaused
        );
      }

      updateAutoplay() {
        this.stopAutoplay();
        if (!this.canAutoplay()) return;
        this.scheduleAutoplay(this.autoplayInterval);
      }

      scheduleAutoplay(delay) {
        this.stopAutoplay();
        if (!this.canAutoplay()) return;
        const safeDelay = Math.max(0, Number(delay) || 0);
        this.autoplayDeadline = Date.now() + safeDelay;
        this.timer = window.setTimeout(() => {
          this.autoplayDeadline = null;
          this.activate((this.activeIndex + 1) % this.cards.length);
        }, safeDelay);
      }

      stopAutoplay() {
        window.clearTimeout(this.timer);
        this.timer = null;
        this.autoplayDeadline = null;
      }

      pauseAutoplayForGesture() {
        this.gestureAutoplayRemaining = this.timer
          ? Math.max(0, (this.autoplayDeadline || Date.now()) - Date.now())
          : null;
        this.gesturePaused = true;
        window.clearTimeout(this.timer);
        this.timer = null;
        this.autoplayDeadline = null;
      }

      resumeAutoplayAfterGesture() {
        const remaining = this.gestureAutoplayRemaining;
        this.gesturePaused = false;
        this.gestureAutoplayRemaining = null;
        if (!this.canAutoplay()) return;
        this.scheduleAutoplay(remaining === null ? this.autoplayInterval : remaining);
      }

      setPauseState(key, value) {
        this[key] = value;
        this.updateAutoplay();
      }

      handleFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.setPauseState('focusPaused', false);
      }

      handleBlockSelect(event) {
        const card = event.target?.closest?.('[data-craft-card]');
        if (!card || !this.contains(card)) return;
        this.editorPaused = true;
        this.refresh();
        this.activate(this.cards.indexOf(card));
      }

      handleBlockDeselect(event) {
        if (!event.target || !this.contains(event.target)) return;
        this.setPauseState('editorPaused', false);
      }

      handleSectionSelect(event) {
        if (!event.target || !this.contains(event.target)) return;
        this.setPauseState('editorPaused', true);
      }

      handleSectionDeselect(event) {
        if (!event.target || !this.contains(event.target)) return;
        this.setPauseState('editorPaused', false);
      }

      handleMutations() {
        window.cancelAnimationFrame(this.mutationFrame);
        this.mutationFrame = window.requestAnimationFrame(() => {
          const activeBlockId = this.cards?.[this.activeIndex]?.dataset.blockId;
          this.refresh();
          if (!this.cards.length) {
            this.stopAutoplay();
            return;
          }
          if (!this.initialized) {
            this.initialized = true;
            this.classList.add('is-enhanced');
            this.observeVisibility();
          }
          const nextIndex = Math.max(0, this.cards.findIndex((card) => card.dataset.blockId === activeBlockId));
          this.activate(nextIndex);
        });
      }
    }
  );
}
